import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ProjectPropertyFormPane } from '@/components/ProjectPropertyFormPane';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import type { ProjectPropertyRow, ProjectPropertyTableConfig } from '@/lib/projectPropertiesConfig';
import { projectPropertiesService } from '@/services/projectPropertiesService';
import {
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '@/theme/agGridTheme';

type PanelMode = 'empty' | 'view' | 'edit' | 'add';

interface ProjectPropertyManageDrawerProps {
  config: ProjectPropertyTableConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ProjectPropertyManageDrawer: React.FC<ProjectPropertyManageDrawerProps> = ({
  config,
  open,
  onOpenChange,
}) => {
  const queryClient = useQueryClient();
  const justSavedRef = useRef(false);

  const {
    data: rows = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [config.queryKey],
    queryFn: () => projectPropertiesService.getAll(config),
    refetchOnMount: 'always',
    enabled: open,
  });

  const [panelMode, setPanelMode] = useState<PanelMode>('empty');
  const [selectedRow, setSelectedRow] = useState<ProjectPropertyRow | null>(null);
  const [editingRow, setEditingRow] = useState<ProjectPropertyRow | null>(null);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [config.queryKey] });
  }, [queryClient, config.queryKey]);

  const handleAdd = () => {
    setEditingRow(null);
    setPanelMode('add');
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Delete this ${config.title.toLowerCase()} row?`)) return;
    try {
      await projectPropertiesService.delete(config, id);
      invalidate();
      setSelectedRow(null);
      setPanelMode('empty');
    } catch (err) {
      toast.error(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleRowClicked = useCallback((event: RowClickedEvent<ProjectPropertyRow>) => {
    if (!event.data) return;
    setSelectedRow(event.data);
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handleAddClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      return;
    }
    setPanelMode('empty');
    setEditingRow(null);
  }, []);

  const handleEditClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingRow(null);
      return;
    }
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handleViewClose = useCallback(() => {
    setPanelMode('empty');
    setSelectedRow(null);
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () =>
      config.gridColumns.map((col) => ({
        ...col,
        valueFormatter: (p) => p.value || '-',
      })),
    [config.gridColumns]
  );

  const gridOptions = useMemo(
    () => ({
      ...agGridDefaultOptions,
      onRowClicked: handleRowClicked,
      rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    }),
    [handleRowClicked]
  );

  const detailOpen = panelMode !== 'empty';

  const handleDialogOpenChange = (next: boolean) => {
    if (!next) {
      setPanelMode('empty');
      setSelectedRow(null);
      setEditingRow(null);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="flex h-[90vh] max-h-[90vh] w-[calc(100%-2rem)] max-w-7xl flex-col gap-0 overflow-hidden p-0 sm:max-w-7xl"
        showCloseButton
      >
        <DialogHeader className="shrink-0 border-b border-border-default px-4 py-3">
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 overflow-hidden p-4">
          <style>{agGridThemeStyles}</style>
          <div className="flex min-h-0 w-full gap-0">
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden pr-4">
              <div className="mb-4 flex shrink-0 items-center justify-between">
                <h3 className="m-0 text-lg font-semibold text-primary">{config.title}</h3>
                <Button type="button" onClick={handleAdd}>
                  <PlusIcon data-icon="inline-start" />
                  Add
                </Button>
              </div>
              {isLoading ? (
                <div className="flex flex-col items-center justify-center gap-4 p-8 text-text-secondary">
                  <Spinner className="size-10" />
                  <p>Loading…</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center p-8 text-danger-600">
                  <p>Error: {error.message}</p>
                  <Button type="button" className="mt-4" onClick={() => void refetch()}>
                    Retry
                  </Button>
                </div>
              ) : rows.length === 0 ? (
                <p className="p-8 text-center text-text-secondary">No rows yet. Click Add.</p>
              ) : (
                <div
                  className="ag-theme-material min-h-0 w-full flex-1"
                  style={agGridSettingsContainerStyles}
                >
                  <AgGridReact
                    theme={agGridTheme}
                    rowData={rows}
                    columnDefs={columnDefs}
                    defaultColDef={agGridDefaultColDef}
                    gridOptions={gridOptions}
                    domLayout="normal"
                    rowNumbers
                    getRowId={(params) => String(params.data.id)}
                  />
                </div>
              )}
            </div>
            <aside
              className={`flex w-[400px] min-w-[400px] shrink-0 flex-col overflow-hidden border-l border-border-default bg-white ${
                detailOpen ? '' : 'items-center justify-center'
              }`}
            >
              {panelMode === 'add' ? (
                <ProjectPropertyFormPane
                  config={config}
                  isOpen={true}
                  onClose={handleAddClose}
                  onSave={() => {}}
                  onEntrySaved={() => {
                    justSavedRef.current = true;
                    invalidate();
                    setPanelMode('empty');
                    setSelectedRow(null);
                    setEditingRow(null);
                  }}
                  row={null}
                  isEdit={false}
                />
              ) : panelMode === 'edit' && editingRow ? (
                <ProjectPropertyFormPane
                  config={config}
                  isOpen={true}
                  onClose={handleEditClose}
                  onSave={(row) => setSelectedRow(row)}
                  onEntrySaved={() => {
                    justSavedRef.current = true;
                    invalidate();
                    setPanelMode('view');
                    setEditingRow(null);
                  }}
                  row={editingRow}
                  isEdit
                />
              ) : panelMode === 'view' && selectedRow ? (
                <ProjectPropertyFormPane
                  key={selectedRow.id}
                  config={config}
                  isOpen={true}
                  readOnly
                  row={selectedRow}
                  onClose={handleViewClose}
                  onSave={() => {}}
                  onEditRequest={() => {
                    setEditingRow(selectedRow);
                    setPanelMode('edit');
                  }}
                  onDeleteRequest={() => void handleDelete(selectedRow.id)}
                />
              ) : (
                <p className="p-6 text-center text-sm text-text-secondary">
                  Select a row to view, or click Add.
                </p>
              )}
            </aside>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
